/**
 * Created by Thomas Mark
 * Date: 2019-10-29
 * 
 * Einzelne Icons verwalten
 * 
 * In diesem Dialog können einzelne Icons in einem Modal-Dialog angezeigt,
 * erstellt, geändert oder gelöscht werden.
 * 
 * Der Dialog kann nur durch einen Administrator gestartet werden.
 * 
 * Änderungen werden auf dem Server in der Datei 'icons.json' gespeichert.
 *
 */
import React from 'react';
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import { DropzoneArea } from 'material-ui-dropzone';
import MovieIcon from '@material-ui/icons/Movie';

import { ModalTitle } from "../../partials/ModalTitle";
import { FabIconDelete } from "../../partials/Icons";
import * as appActions from "../../../store/actions/appActions";
import * as iconActions from "../../../store/actions/iconActions";
import * as utils from "../../../basics/utils";
import * as cordova from "../../../basics/cordova";

class IconModal extends React.Component {
    constructor(props) {
        super(props);

        this.component = 'IconModal'

        this.state = {
            newIcon: { id: '', type: 'dialog', svg: '', label: '', unit: '', value: '' }
        }
    }

    // ==========================================================================
    //
    // LIFESYCLE - SECTION
    //
    // ==========================================================================

    componentDidUpdate(prevProps) {
        const { modalDialog } = this.props.app;

        if (modalDialog.dialog === this.component && modalDialog.dialog !== prevProps.app.modalDialog.dialog) {
            this.setState({ newIcon: this.props.icon.currentIcon })
        }
    }

    // ==========================================================================
    //
    // RENDER - SECTION
    //
    // ==========================================================================
    render() {
        const { modalDialog, unitHeight } = this.props.app;
        const { newIcon } = this.state;
        const { icon } = this.props;

        const onSave = () => {
            if (modalDialog.type === 'create') {
                if (!newIcon['svg']) newIcon['svg'] = '';
                this.props.createIcon(newIcon);
            }
            if (modalDialog.type === 'delete') this.props.deleteIcon(newIcon.id);
            if (modalDialog.type === 'update') this.props.updateIcon(newIcon.id, newIcon);
        }

        const onCancel = () => {
            this.props.closeModalDialog(); // appAction.js
        }

        const onChange = (e) => {
            this.setState({ newIcon: { ...newIcon, [e.target.id]: e.target.value } });
        }

        const onSubmit = (e) => {
            e.preventDefault();
        }

        const onChangeType = (e) => {
            this.setState({ newIcon: { ...newIcon, type: e.target.value } });
        }

        const getError = (id) => {

            const noError = { error: false, message: '' }

            if (!icon['error']) return noError;
            if (!icon.error['data']) return noError;
            if (!icon.error.data['errors']) return noError;
            if (!icon.error.data.errors.length === 0) return noError;

            const errorFound = icon.error.data.errors.filter((d) => d.param === id)
            if (errorFound.length > 0) {
                return { error: true, message: errorFound[0].msg };
            }
            return noError;
        }

        const renderTextField = (id, label, type, autoFocus, disabled) => {

            return (
                <TextField
                    autoFocus={autoFocus}
                    id={id}
                    label={label}
                    type={type}
                    fullWidth
                    disabled={disabled}
                    value={newIcon[id]}
                    onChange={onChange}
                    InputLabelProps={{ shrink: true }}
                    margin="normal"
                    error={getError(id).error}
                    helperText={getError(id).message}
                />
            )
        }

        const renderIcon = () => {

            const imageData = newIcon['svg'];

            const style = utils.getStyleHeight(unitHeight, 2.0, 0.3, 0.3);

            if (imageData === '' || imageData === undefined) {

                const onUpload = (files) => {
                    if (files.length === 0) return;

                    cordova.data2type(files[0], 'Text').then((svgText) => {
                        this.setState({ newIcon: { ...newIcon, svg: svgText } });
                    })
                }

                return (
                    <div className="w-2/12" style={style}>
                        <DropzoneArea
                            Icon={MovieIcon}
                            acceptedFiles={['image/svg+xml']}
                            filesLimit={1}
                            showAlerts={true}
                            showPreviews={false}
                            showPreviewsInDropzone={false}
                            dropzoneText={"drag & drop"}
                            onChange={(files) => onUpload(files)}
                            onAlert={(message, variant) => console.log(`${variant}: ${message}`)}
                        />
                    </div>
                )
            } else {
                const src = URL.createObjectURL(new Blob([imageData], { type: 'image/svg+xml' }));

                const onDelete = () => {
                    this.setState({ newIcon: { ...newIcon, svg: '' } });
                }

                return (
                    <div className="w-full mx-auto" style={style}>
                        <div className="absolute mt-12 w-full">
                            <img src={src} width={128} height={128} title="svg-image" alt="" />
                        </div>
                        {modalDialog.type !== 'delete' &&
                            <div className="relative top-0 w-full">
                                {FabIconDelete("Icon entfernen", onDelete)}
                            </div>
                        }
                    </div>
                )
            }
        }

        // ==========================================================================
        // 
        // MAIN RENDER
        // 
        // ==========================================================================
        if (modalDialog.dialog !== this.component) return null;

        let dialogTitle = '';
        if (modalDialog.type === 'create') dialogTitle = 'Icon erstellen';
        if (modalDialog.type === 'update') dialogTitle = 'Icon ändern';
        if (modalDialog.type === 'delete') dialogTitle = 'Icon löschen';

        const disabled = modalDialog.type === 'delete';

        return (
            <div>
                <Dialog open={true} onClose={onCancel} fullWidth={true} maxWidth={'md'} >
                    <ModalTitle onClose={onCancel}>{dialogTitle}</ModalTitle>
                    <DialogContent dividers>
                        <form className={"w-full"} noValidate onSubmit={onSubmit}>
                            <FormControl className={"w-full"}>

                                {renderTextField('id', 'ID', 'text', false, modalDialog.type === 'create' ? false : true)}

                                <RadioGroup id="type" name="type" row value={newIcon.type} onChange={onChangeType}>
                                    <FormControlLabel disabled={disabled} value="dialog" control={<Radio color="primary" />} label="Dialog" />
                                    <FormControlLabel disabled={disabled} value="stapler" control={<Radio color="primary" />} label="Stapler" />
                                    <FormControlLabel disabled={disabled} value="product" control={<Radio color="primary" />} label="Produkt" />
                                </RadioGroup>

                                {renderTextField('label', 'Label', 'text', true, disabled)}
                                {renderTextField('unit', 'Einheit', 'text', false, disabled)}
                                {renderTextField('value', 'Wert', 'text', false, disabled)}

                                {renderIcon()}

                            </FormControl>
                        </form>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={onCancel} variant="outlined" color="default">Abbruch</Button>
                        <div className="flex-grow"></div>
                        <Button onClick={onSave} variant="contained" color="primary">OK</Button>
                    </DialogActions>
                    <div className={"w-full text-red-400 p-2"}>
                        {icon.error !== null ? icon.error.data.message : <br />}
                    </div>
                </Dialog>
            </div>
        );
    }
}

function mapStateToProps(state) {
    return {
        app: state.app,
        icon: state.icon,
    }
}

function mapDispatchToProps(dispatch) {
    return bindActionCreators(Object.assign({}, appActions, iconActions), dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(IconModal);